import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
   connectedCallback() {
     super.connectedCallback();

     this.#showLoader();

     const { signal } = this.#controller;
     const target = this.closest('.shopify-section, dialog');

     target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
     this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
       signal,
     });

     setTimeout(() => {
       this.#hideDuplicateImages();
       this.addToggleButton();
     }, 100);
   }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
   #handleVariantUpdate = (event) => {
     const source = event.detail.data.html;

     if (!source) return;
     const newMediaGallery = source.querySelector('media-gallery');

     if (!newMediaGallery) return;

     this.#showLoader();

     this.replaceWith(newMediaGallery);
     
     setTimeout(() => {
       this.#hideDuplicateImages();
       const currentGallery = document.querySelector('media-gallery');
       if (currentGallery) {
         const existingButton = currentGallery.querySelector('.media-gallery-toggle-button');
         if (existingButton) {
           existingButton.remove();
         }
         currentGallery.dataset.expanded = 'false';
         currentGallery.addToggleButton();
       }
     }, 100);
   };

  #showLoader = () => {
    let loader = this.querySelector('.media-gallery-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'media-gallery-loader';
      loader.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: white;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      this.style.position = 'relative';
      this.appendChild(loader);
    }
    loader.style.display = 'flex';
  };

  #hideLoader = () => {
    const loader = this.querySelector('.media-gallery-loader');
    if (loader) {
      loader.style.display = 'none';
    }
  };

   #hideDuplicateImages = () => {
     try {
      const mediaGallery = document.querySelector('media-gallery');
      if (!mediaGallery) {
        console.log('No se encontró media-gallery');
        return;
      }

      const firstImage = mediaGallery.querySelector('img[src*="files"]') || 
                        mediaGallery.querySelector('img[src*="products"]') ||
                        mediaGallery.querySelector('.media img') ||
                        mediaGallery.querySelector('img');
      
      if (!firstImage) {
        console.log('No se encontró la primera imagen');
        return;
      }

      const firstImageSrc = firstImage?.src;
      
      const urlWithoutParams = firstImageSrc.split('?')[0];
      const fileName = urlWithoutParams.split('/').pop();
      
      let baseName = '';
      

      let baseNameMatch = fileName.match(/(.+)_\d+\.(png|jpg|jpeg|webp)$/i);
      if (baseNameMatch) {
        baseName = baseNameMatch[1];
      } else {
        baseNameMatch = fileName.match(/(.+)-\d+\.(png|jpg|jpeg|webp)$/i);
        if (baseNameMatch) {
          baseName = baseNameMatch[1];
        } else {

          baseNameMatch = fileName.match(/(.+)\d{2,3}\.(png|jpg|jpeg|webp)$/i);
          if (baseNameMatch) {
            baseName = baseNameMatch[1];
          } else {

            baseNameMatch = fileName.match(/(.+)\d+\.(png|jpg|jpeg|webp)$/i);
            if (baseNameMatch) {
              baseName = baseNameMatch[1];
            } else {
              baseNameMatch = fileName.match(/(.+)\.(png|jpg|jpeg|webp)$/i);
              if (baseNameMatch) {
                baseName = baseNameMatch[1];
              } else {
                console.log('No se pudo extraer el nombre base de la imagen con ningún patrón');
                console.log('Formato de archivo no reconocido:', fileName);
                return;
              }
            }
          }
        }
      }

      const allImages = document.querySelectorAll('media-gallery img[src*="files"], media-gallery img[src*="products"]');
      console.log(`Total de imágenes encontradas: ${allImages.length}`);

      let hiddenCount = 0;
      let shownCount = 0;

      allImages.forEach((img, index) => {
        const imgSrc = img.src;
        const imgUrlWithoutParams = imgSrc.split('?')[0];
        
        if (imgUrlWithoutParams.includes(baseName)) {

          img.classList.remove('hidden-variant');
          
          const parentContainer = img.closest('li');
          if (parentContainer) {
            parentContainer.style.display = '';
            parentContainer.classList.remove('hidden-variant');
          }
          
          shownCount++;
          console.log(`Imagen mostrada (${index + 1}):`, imgUrlWithoutParams);
        } else {
          img.style.display = 'none';
          img.classList.add('hidden-variant');
          
          const parentContainer = img.closest('li');
          if (parentContainer) {
            parentContainer.style.display = 'none';
            parentContainer.classList.add('hidden-variant');
            console.log(`Contenedor ocultado:`, parentContainer.tagName, parentContainer.className);
          }
          
          hiddenCount++;
          console.log(`Imagen ocultada (${index + 1}):`, imgUrlWithoutParams);
        }
      });

      console.log(`Total de imágenes mostradas: ${shownCount}`);
      console.log(`Total de imágenes ocultadas: ${hiddenCount}`);

      this.#processZoomDialog(baseName);

      this.#hideLoader();

    } catch (error) {
      console.error('Error al filtrar imágenes por variante:', error);
      this.#hideLoader();
    }
  };

  #processZoomDialog = (baseName) => {
    try {
      const zoomDialog = document.querySelector('.dialog-zoomed-gallery');
      if (!zoomDialog) {
        console.log('No se encontró dialog-zoomed-gallery');
        return;
      }

      const zoomImages = zoomDialog.querySelectorAll('img[src*="files"], img[src*="products"]');
      console.log(`Imágenes en dialog zoom: ${zoomImages.length}`);

      zoomImages.forEach((img, index) => {
        const imgUrlWithoutParams = img.src.split('?')[0];
        const parentLi = img.closest('li');
        
        if (imgUrlWithoutParams.includes(baseName)) {
          if (parentLi) {
            parentLi.style.display = '';
            parentLi.classList.remove('hidden-variant');
            console.log(`LI mostrado en zoom dialog (${index + 1}):`, parentLi);
          }
        } else {
          if (parentLi) {
            parentLi.style.display = 'none';
            parentLi.classList.add('hidden-variant');
            console.log(`LI ocultado en zoom dialog (${index + 1}):`, parentLi);
          }
        }
      });

    } catch (error) {
      console.error('Error al procesar dialog zoom:', error);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    console.log('REFS SLIDESHOW', this.refs.slideshow);
    return this.refs.slideshow;
  }

  get media() {
    console.log('REFS MEDIA', this.refs.media);
    return this.refs.media;
  }

  get presentation() {
    console.log('DATASET PRESENTATION', this.dataset.presentation);
    return this.dataset.presentation;
  }

   toggleMediaGallery() {
     const visibleGridItems = this.querySelectorAll('.media-gallery__grid li:not(.hidden-variant)');
     const isExpanded = this.dataset.expanded === 'true';
     
     console.log(`Toggle: ${visibleGridItems.length} imágenes visibles, estado expandido: ${isExpanded}`);
     
     if (isExpanded) {
       this.collapseToFourImages();
     } else {
       this.expandToAllImages();
     }
   }

   collapseToFourImages() {
     const visibleGridItems = this.querySelectorAll('.media-gallery__grid li:not(.hidden-variant)');
     console.log(`Contrayendo a 4 imágenes de ${visibleGridItems.length} visibles`);
     
     visibleGridItems.forEach((item, index) => {
       if (index >= 4) {
         item.style.display = 'none';
         item.classList.add('hidden-extra');
       } else {
         item.style.display = '';
         item.classList.remove('hidden-extra');
       }
     });
     
     this.dataset.expanded = 'false';
     this.updateToggleButton();
   }

   expandToAllImages() {
     const visibleGridItems = this.querySelectorAll('.media-gallery__grid li:not(.hidden-variant)');
     console.log(`Expandiendo todas las ${visibleGridItems.length} imágenes visibles`);
     
     visibleGridItems.forEach(item => {
       item.style.display = '';
       item.classList.remove('hidden-extra');
     });
     
     this.dataset.expanded = 'true';
     this.updateToggleButton();
   }

  updateToggleButton() {
    const isExpanded = this.dataset.expanded === 'true';
    const button = this.querySelector('.media-gallery-toggle-button');
    
    if (button) {
      button.textContent = isExpanded ? 'Hide' : 'View All';
    }
  }

   addToggleButton() {
     const gridItems = this.querySelectorAll('.media-gallery__grid li:not(.hidden-variant)');
     console.log(`Reconteo de imágenes visibles: ${gridItems.length}`);
     
     if (gridItems.length > 4) {
       if (!this.querySelector('.media-gallery-toggle-button')) {
         const button = document.createElement('button');
         button.className = 'media-gallery-toggle-button button button-secondary';
         button.textContent = 'View All';
         button.style.margin = '0 auto';
         button.style.borderRadius = '0';
         
         button.addEventListener('click', () => {
           this.toggleMediaGallery();
         });
         
         const grid = this.querySelector('.media-gallery__grid');
         if (grid && grid.parentNode) {
           grid.parentNode.insertBefore(button, grid.nextSibling);
         }
       }
       
       this.dataset.expanded = 'false';
       this.collapseToFourImages();
     } else {
       const existingButton = this.querySelector('.media-gallery-toggle-button');
       if (existingButton) {
         existingButton.remove();
       }
       this.expandToAllImages();
     }
   }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}